import { useTranslation } from "react-i18next";
import { ChevronsUpDown, KeyRound, LogOut, Power, User as UserIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth, selectUser } from "@/shared/auth/store";
import { useLogout } from "@/shared/auth/useLogout";
import { exitApp, isNativeApp } from "@/shared/native/appExit";
import { ROLE_LABEL_KEY } from "@/shared/auth/permissions";
import { Avatar, AvatarFallback } from "@/shared/ui/Avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/DropdownMenu";

/**
 * AccountMenu — sits at the bottom of the sidebar, the "primary identity
 * surface" the operator returns to between tasks.
 *
 *   * Trigger row: avatar + name + role + chevron. Click anywhere fires it.
 *   * Menu opens upward (side="top") because the trigger is at the bottom.
 *   * 항목은 **나에 관한 것만** — 내 계정 / 비밀번호 변경 / 로그아웃.
 *     시스템 설정은 사이드바에 있으므로 여기 두지 않는다.
 *   * 로그아웃은 `useLogout()` 공용 훅 — MoreSheet(모바일)와 같은 동작.
 */
export function AccountMenu() {
  const user = useAuth(selectUser);
  const navigate = useNavigate();
  const { t } = useTranslation();
  // 로그아웃 동작은 모바일 더보기 시트와 공유한다 — 두 곳이 갈라지면
  // 한쪽만 세션을 지우는 사고가 난다.
  const logoutM = useLogout();

  if (!user) return null;
  const initials = (user.username ?? "?").slice(0, 2).toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={
            "w-full flex items-center gap-3 px-2.5 py-2 rounded-[var(--radius-lg)] " +
            "text-left outline-none " +
            "transition-colors duration-[var(--motion-fast)] ease-[var(--ease-out-soft)] " +
            "hover:bg-[var(--bg-hover)] focus-visible:bg-[var(--bg-hover)] " +
            "focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          }
        >
          <Avatar className="h-9 w-9 text-[13px] shrink-0">
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <span className="flex-1 min-w-0 leading-tight">
            <span className="block text-[14px] font-semibold tracking-[-0.01em] text-[var(--fg)] truncate">
              {user.username}
            </span>
            <span className="block text-[11.5px] text-[var(--fg-subtle)] truncate">
              {t(ROLE_LABEL_KEY[user.role])}
              {user.organization?.name ? ` · ${user.organization.name}` : ""}
            </span>
          </span>
          <ChevronsUpDown
            size={14}
            strokeWidth={1.75}
            className="text-[var(--fg-subtle)] shrink-0"
          />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent side="top" align="end" sideOffset={8} className="min-w-[240px]">
        <DropdownMenuLabel>{user.email}</DropdownMenuLabel>

        <DropdownMenuGroup>
          {/* 여기는 **신원** 표면이다 — 나에 관한 것만 둔다. 시스템 설정은
              사이드바(설정·관리 그룹)와 ⌘, 로 이미 닿으므로 같은 목적지를
              메뉴에 두 번 두지 않는다. */}
          <DropdownMenuItem onSelect={() => navigate("/profile")}>
            <UserIcon size={14} strokeWidth={1.75} className="opacity-80" />
            <span>{t("widgets.account.profile")}</span>
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => navigate("/change-password")}>
            <KeyRound size={14} strokeWidth={1.75} className="opacity-80" />
            <span>{t("widgets.account.changePassword")}</span>
          </DropdownMenuItem>
        </DropdownMenuGroup>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          destructive
          onSelect={() => logoutM.mutate()}
          disabled={logoutM.isPending}
        >
          <LogOut size={14} strokeWidth={1.75} />
          <span>{t("widgets.account.logout")}</span>
        </DropdownMenuItem>

        {/* 앱 종료 — 네이티브 앱에서만. 세션은 그대로 두고 프로세스만 끝낸다
            (자동 로그인 보드에서 다시 켜면 그대로 들어온다). */}
        {isNativeApp() ? (
          <DropdownMenuItem onSelect={() => void exitApp()}>
            <Power size={14} strokeWidth={1.75} className="opacity-80" />
            <span>{t("widgets.account.exitApp")}</span>
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
